import { useState, useEffect, useCallback, useContext } from 'react';
import useEmployeeStore from '../store/employeeStore';
import useDebounce from '../../../../hooks/useDebounce';
import { EmployeeSearchContext } from '../contexts/EmployeeSearchProvider';

/**
 * Custom hook to fetch and manage employees data with infinite scrolling
 * Automatically detects if running in a context provider and uses independent store
 * 
 * @param {Boolean} includeAllOption - Whether to include an "All Employees" option
 * @param {String} searchTerm - Initial search term
 * @returns {Object} - Employees data, loading state, and more
 */
const useEmployees = (includeAllOption = false, searchTerm = '') => {
  const [localSearch, setLocalSearch] = useState(searchTerm);
  const debouncedSearchTerm = useDebounce(localSearch, 300);
  
  // Try to get context store first, fallback to global store
  const contextStore = useContext(EmployeeSearchContext);
  const useStore = contextStore || useEmployeeStore;
  
  // Select individual primitives instead of objects to avoid re-renders
  const employees = useStore(state => state.employees);
  const loading = useStore(state => state.loading);
  const hasNextPage = useStore(state => state.hasNextPage);
  const error = useStore(state => state.error);
  
  // For methods, we don't need to worry about re-renders since references are stable
  const initialize = useStore(state => state.initialize);
  const loadMore = useStore(state => state.loadMore);
  const searchEmployees = useStore(state => state.searchEmployees);
  const reset = useStore(state => state.reset);
  
  // Initialize on mount
  useEffect(() => {
    initialize({ search: searchTerm });
    
    // Cleanup on unmount
    return () => reset();
  }, [initialize, reset, searchTerm]);
  
  // Run search when debounced value changes
  useEffect(() => {
    searchEmployees(debouncedSearchTerm);
  }, [debouncedSearchTerm, searchEmployees]);
  
  // Function to handle search input changes
  const handleSearchChange = useCallback((value) => {
    setLocalSearch(value);
  }, []);
  
  // Function to check if we need to load more results
  const handleScroll = useCallback((event) => {
    if (!hasNextPage || loading) return;
    
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    
    // Load more when the user scrolls to bottom (with a margin)
    if (scrollHeight - scrollTop <= clientHeight * 1.2) {
      loadMore();
    }
  }, [hasNextPage, loading, loadMore]);
  
  const getEmployeeOptions = useCallback(() => {
    // Add "All Employees" option at the beginning of the list if requested
    return includeAllOption 
      ? [{
          id: 'all',
          fullName: 'All Individuals',
          isAllEmployeesOption: true
        }, ...employees]
      : employees;
  }, [employees, includeAllOption]);

  return {
    employees,
    loading,
    hasNextPage,
    error,
    getEmployeeOptions,
    handleScroll,
    handleSearchChange,
    searchTerm: localSearch
  };
};

export default useEmployees; 